import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Progress } from '../ui/progress';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PremiumButton } from '../ui/premium-button';
import { useAuth } from '@/contexts/AuthContext';

export const UsageMeter: React.FC = () => {
    const navigate = useNavigate();
    const { user, currentOrganization } = useAuth();
    const { data: usage, isLoading, isError } = useQuery({
        queryKey: ['billing-usage', currentOrganization?.id],
        queryFn: () => api.request<any>('/api/billing/usage'),
        enabled: !!user && !!currentOrganization,
        // Refresh often to show real-time updates after scans
        refetchInterval: 30000,
    });

    if (isLoading) {
        return <div className="p-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>;
    }

    if (isError || !usage) {
        return null;
    }

    // If unlimited, don't show meter, maybe show Plan badge
    if (usage.scan_limit === -1) {
        return (
            <div className="px-4 py-2">
                <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium text-muted-foreground">Current Plan</span>
                    <span className="font-bold text-primary">{usage.plan_tier}</span>
                </div>
                <div className="text-xs text-muted-foreground">Unlimited Manual Scans</div>
            </div>
        );
    }

    const percent = Math.min(100, Math.max(0, ((usage.scan_limit - usage.scans_remaining) / usage.scan_limit) * 100));
    const isLow = usage.scans_remaining <= 2;
    const isEmpty = usage.scans_remaining === 0;

    return (
        <div className="px-2 py-1 space-y-1">
            <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-muted-foreground">Manual Scans</span>
                <span className={`font-medium ${isLow ? 'text-destructive' : 'text-foreground'}`}>
                    {usage.scan_limit - usage.scans_remaining} / {usage.scan_limit}
                </span>
            </div>

            <Progress value={percent} className={`h-1.5 ${isLow ? 'bg-destructive/20' : ''}`} indicatorClassName={isLow ? 'bg-destructive' : ''} />

            {isEmpty ? (
                <div className="bg-destructive/10 text-destructive text-xs p-2 rounded flex items-start gap-2 mt-2">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <span className="font-semibold block mb-1">Limit Reached</span>
                        <span className="leading-tight block mb-2">Upgrade to Pro for unlimited scans.</span>
                        <PremiumButton size="sm" className="w-full h-7 text-xs" onClick={() => navigate('/settings/billing')}>
                            Upgrade Now
                        </PremiumButton>
                    </div>
                </div>
            ) : (
                <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Resets in {getDaysUntilReset()} days</span>
                    {isLow && (
                        <PremiumButton size="icon" className="h-6 w-16 text-[10px]" onClick={() => navigate('/settings/billing')}>
                            Upgrade
                        </PremiumButton>
                    )}
                </div>
            )}
        </div>
    );
};

function getDaysUntilReset() {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const diffTime = Math.abs(nextMonth.getTime() - now.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
}
